import { Util } from "../../../../util/util";
import { BaseTriggerItemsQualifier } from "./base-trigger-items";
import { BogoTriggerItemSelector } from "./bogo/bogo-trigger-item-selector";
// Buy One Get One fills its two positions from the first two groups; any further group is the
// coupon's own to qualify.
const BOGO_POSITION_COUNT = 2;
export class GroupItemBaseTriggerItemsQualifier extends BaseTriggerItemsQualifier {
    _getTriggerItems(coupon, cart) {
        const groups = this._groupCouponItems(coupon);
        return coupon.isBogo
            ? this._getBogoTriggerItems(coupon, cart, groups)
            : this._getStandardTriggerItems(coupon, cart, groups);
    }
    _groupCouponItems(coupon) {
        const groups = new Map();
        for (const item of coupon.items) {
            const arr = groups.get(item.groupIndex);
            if (arr)
                arr.push(item);
            else
                groups.set(item.groupIndex, [item]);
        }
        return [...groups];
    }
    _getStandardTriggerItems(coupon, cart, groups) {
        const validItems = [];
        for (const [groupIndex, couponGroupItems] of groups) {
            const validGroupItems = this._qualifyCouponItemsGroupQTY(groupIndex, coupon, cart, couponGroupItems);
            if (!validGroupItems?.length)
                return [];
            validItems.push(...validGroupItems);
        }
        return validItems;
    }
    /**
     * Buy One Get One qualification (ADO #84401): the selector fills the buying and discounted
     * positions from the first two groups, then any further group is qualified by the coupon's
     * own application, as it is without Buy One Get One. Returning [] anywhere means the coupon
     * does not apply — nothing is released because only this qualifier's cart copy was marked.
     */
    _getBogoTriggerItems(coupon, cart, groups) {
        if (groups.length < BOGO_POSITION_COUNT)
            return [];
        const [buyingGroup, discountedGroup] = groups;
        const pair = new BogoTriggerItemSelector(this._asBogoCandidateSource())
            .select(coupon, cart, buyingGroup, discountedGroup);
        if (!pair)
            return [];
        const validItems = [pair.buying, pair.discounted];
        for (const [groupIndex, couponGroupItems] of groups.slice(BOGO_POSITION_COUNT)) {
            const validGroupItems = this._qualifyCouponItemsGroupQTY(groupIndex, coupon, cart, couponGroupItems);
            if (!validGroupItems?.length)
                return [];
            validItems.push(...validGroupItems);
        }
        return validItems;
    }
    /** Lends the selector this qualifier's cart copy, with a qualifying-item group as the pool. */
    _asBogoCandidateSource() {
        return {
            cartItems: this._cartItems,
            collectCandidates: (group, coupon, cart) => this._collectBogoCandidates(group, coupon, cart)
        };
    }
    /** Every order item the group's rows can match, each marked as taken. */
    _collectBogoCandidates([groupIndex, rows], coupon, cart) {
        const candidates = [];
        // Each configured row counts for a quantity of 1 while Buy One Get One is enabled, but the
        // same row can match several order items, so keep re-searching it until it runs dry.
        for (const row of rows) {
            let candidate = this._qualifyCouponItemsGroup(groupIndex, coupon, cart, row);
            while (candidate) {
                candidates.push(candidate);
                candidate = this._qualifyCouponItemsGroup(groupIndex, coupon, cart, row);
            }
        }
        return candidates;
    }
    /**
     * Undoes the marking _findQualifyingItem did for trigger items the caller decided not to keep,
     * so a rejected match doesn't leave the items looking already discounted to the rows and
     * coupons that come after it.
     */
    _releaseTriggerItems(triggerItems) {
        for (const tItem of triggerItems) {
            const cartItem = this._cartItems[tItem.index];
            if (!cartItem)
                continue;
            if (cartItem.halfCount > 1) {
                for (const half of this._cartItems) {
                    if (half.itemKey === cartItem.itemKey)
                        half.discountedMarkedQuantity -= 1;
                }
            }
            else {
                cartItem.discountedMarkedQuantity -= 1;
            }
        }
    }
    _qualifyCouponItemsGroup(group, coupon, cart, cItem) {
        const qItem = this._findQualifyingItem(group, coupon, cart, cItem);
        return qItem && qItem.index > -1 ? qItem : null;
    }
    _findQualifyingItem(group, coupon, cart, cItem) {
        const cItemId = cItem.itemId;
        const cSizeId = cItem.sizeId;
        const cModifierCount = cItem.modifierCount;
        const cAddOnPrice = cItem.addOnPrice || 0;
        let exactMatchFound = false;
        let items = this._cartItems.filter(x => this._isItemEligible(x, coupon, cart, cItemId, cSizeId) &&
            this._modifierEquals(x, cModifierCount));
        if (items.length === 0) {
            items = this._cartItems.filter(x => this._isItemEligible(x, coupon, cart, cItemId, cSizeId) &&
                this._modifierGreater(x, cModifierCount));
        }
        else {
            exactMatchFound = true;
        }
        // For half & half items, every half must independently meet the coupon's
        // qualifying-items criteria before the item can be treated as a match. A
        // single qualifying half no longer grants the discount to its siblings.
        const cartItem = items.find(x => x.halfCount <= 1 || this._allHalvesQualify(group, coupon, cart, x));
        const matchingItemIndex = cartItem ? this._cartItems.indexOf(cartItem) : -1;
        if (matchingItemIndex === -1 && cModifierCount > 0 && ((!coupon.exactMatch || !coupon.autoApplyExactMatch))) {
            const newModCount = cModifierCount - 1;
            const cItemOverride = coupon.items.find(x => x.groupIndex === group && x.itemId == cItemId && x.modifierCount === newModCount);
            const cItemClone = structuredClone(cItem);
            cItemClone.modifierCount = newModCount;
            cItemClone.addOnPrice = cItemOverride?.addOnPrice ?? cAddOnPrice;
            return this._findQualifyingItem(group, coupon, cart, cItemClone);
        }
        if (!cartItem || items.length === 0 || (coupon.exactMatch && !exactMatchFound)) {
            return null;
        }
        if (cartItem.halfCount > 1) {
            cart.markAllHalves(this._cartItems, cartItem.itemKey);
        }
        else {
            cartItem.discountedMarkedQuantity += 1;
        }
        return {
            index: matchingItemIndex,
            modifierCount: cModifierCount,
            addOnPrice: cAddOnPrice,
            groupIndex: group
        };
    }
    /**
     * A half & half item qualifies only when every half sharing its itemKey is on the coupon's
     * qualifying-items list for this group and is otherwise eligible. Enforces ADO #82541: all
     * halves must meet criteria rather than inheriting the discount from the first qualifying half.
     *
     * Matching is on item and size alone. A half carrying fewer priced modifiers than its row's
     * Toppings Covered still qualifies — it is priced at the coverage it actually carries, the same
     * way the decrement fallback in _findQualifyingItem prices an under-topped whole item. Only an
     * exact-match coupon holds the halves to the configured count.
     */
    _allHalvesQualify(group, coupon, cart, cartItem) {
        const halves = this._cartItems.filter(x => x.itemKey === cartItem.itemKey);
        // All configured halves must be present in the cart.
        if (halves.length !== cartItem.halfCount)
            return false;
        const groupItems = coupon.items.filter(x => x.groupIndex === group);
        return halves.every(half => groupItems.some(gItem => this._isItemEligible(half, coupon, cart, gItem.itemId, gItem.sizeId) &&
            (!coupon.exactMatch || this._modifierEquals(half, gItem.modifierCount))));
    }
    _isItemEligible(x, coupon, cart, itemId, sizeId) {
        return x.id === itemId &&
            x.sizeId === sizeId &&
            !x.isRemoved &&
            Util.IsItemUnitAvailable(cart, x, coupon.allowOtherCoupons) &&
            (!coupon.allowOtherCoupons || (coupon.allowOtherCoupons && !Util.CheckSameCouponAppliedQtyTimes(cart, x, coupon.couponId))) &&
            (!coupon.excludeLoyaltyUsage || (coupon.excludeLoyaltyUsage && !x.isRedeemed));
    }
    _modifierEquals(x, modifierCount) {
        return Util.GetItemPricedModifierCount(x, this._data.preModifiers) === modifierCount;
    }
    _modifierGreater(x, modifierCount) {
        return Util.GetItemPricedModifierCount(x, this._data.preModifiers) > modifierCount;
    }
}
//# sourceMappingURL=group-item-base-trigger-items.js.map